import { useState, useCallback, useMemo } from 'react';
import { calculatePaymentAmount, describePaymentSelection } from '../services/pagamentoService';
import { validateSection } from '../utils/validation';
import { rolarParaTopo } from '../../../../utils/rolarParaTopo';

// `defaultValue` dos campos do config (ex: radios "Não", País "Brasil").
const coletarValoresPadrao = (steps = []) => {
  const valores = {};
  steps.forEach(step => step.sections.forEach(section => section.fields.forEach(field => {
    if (field.defaultValue !== undefined) valores[field.name] = field.defaultValue;
  })));
  return valores;
};

export const usePedidoForm = (config) => {
  const [currentStep, setCurrentStep] = useState(1);
  // Só o que foi preenchido (pela pessoa, perfil, rascunho ou login). Os
  // `defaultValue` ficam por baixo (formData, abaixo) e só valem pra campo ainda sem
  // valor — assim o perfil, que é mesclado com `{ ...perfil, ...prev }`, continua
  // prevalecendo sobre um padrão (ex: Função Pública "SIM" no cadastro x padrão "NAO").
  const [valoresPreenchidos, setFormData] = useState({});
  const valoresPadrao = useMemo(() => coletarValoresPadrao(config?.steps), [config?.steps]);
  const formData = useMemo(() => ({ ...valoresPadrao, ...valoresPreenchidos }), [valoresPadrao, valoresPreenchidos]);
  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});
  const [documentos, setDocumentos] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [protocoloData, setProtocoloData] = useState(null);
  const [solicitacaoId, setSolicitacaoId] = useState(null);
  // "Nº do Pedido" mostrado pra pessoa (SolicitacaoServico.Protocolo) — diferente do
  // solicitacaoId, que é só o ID interno usado nas chamadas à API.
  const [numeroPedido, setNumeroPedido] = useState(null);
  const [requerimentoId, setRequerimentoId] = useState(null);

  const totalSteps = config ? config.steps?.length || 0 : 0;
  const hasDocuments = Boolean(
    (config?.documentConfig?.required?.length || 0) +
    (config?.documentConfig?.conditional?.length || 0) +
    (config?.documentConfig?.optional?.length || 0)
  );
  // Pedidos sem nenhum documento pulam a etapa de Documentos inteiramente
  // (nunca fica true, e as etapas seguintes "sobem" um número).
  const isDocumentStep = hasDocuments && currentStep === totalSteps + 1;
  const isPagamentoStep = currentStep === totalSteps + (hasDocuments ? 2 : 1);
  const isSuccessStep = currentStep === totalSteps + (hasDocuments ? 3 : 2);

  const paymentAmount = useMemo(
    () => calculatePaymentAmount(config?.paymentConfig, formData),
    [config?.paymentConfig, formData]
  );

  const dynamicPaymentConfig = useMemo(() => {
    if (!config?.paymentConfig) return null;

    const { opcaoSelecionada, opcaoNome, opcaoDescricao } = describePaymentSelection(config.paymentConfig, formData);

    return {
      ...config.paymentConfig,
      amount: paymentAmount,
      selectedOption: opcaoSelecionada,
      selectedOptionDetails: opcaoNome ? { name: opcaoNome, description: opcaoDescricao } : null
    };
  }, [config?.paymentConfig, paymentAmount, formData]);

  const handleChange = useCallback((e) => {
    const { name, value, type, checked } = e.target;
    const newValue = type === 'checkbox' ? checked : value;
    
    setFormData(prev => ({ ...prev, [name]: newValue }));
    
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: null }));
    }
  }, [errors]);

  const handleBlur = useCallback((e) => {
    const { name } = e.target;
    setTouched(prev => ({ ...prev, [name]: true }));
  }, []);

  const nextStep = useCallback(() => {
    setCurrentStep(prev => prev + 1);
    rolarParaTopo();
  }, []);

  const prevStep = useCallback(() => {
    setCurrentStep(prev => prev - 1);
    rolarParaTopo();
  }, []);

  const resetForm = useCallback(() => {
    setCurrentStep(1);
    setFormData({});
    setDocumentos({});
    setProtocoloData(null);
    setSolicitacaoId(null);
    setNumeroPedido(null);
    setRequerimentoId(null);
    setErrors({});
    setTouched({});
    setError(null);
  }, []);

  // Retoma um pedido salvo no navegador (ver rascunhoPedidoService). Os dados
  // do rascunho prevalecem sobre o pré-preenchimento do perfil, que pode ter
  // chegado antes. A etapa é limitada ao Pagamento: Sucesso nunca é retomado.
  // Se alguma etapa do formulário anterior à salva não passa mais na validação
  // (ex: rascunho de quando "Sexo" era texto livre), retoma nela — senão a pessoa
  // cairia direto em Documentos/Pagamento com um dado inválido (que, no caso do
  // sexo, decide se o reservista é exigido).
  const restaurarRascunho = useCallback((rascunho) => {
    const ultimaEtapaRetomavel = totalSteps + (hasDocuments ? 2 : 1);
    const dadosRestaurados = { ...formData, ...rascunho.formData };
    let etapa = Math.min(Math.max(rascunho.currentStep || 1, 1), ultimaEtapaRetomavel);

    const etapaInvalida = (config?.steps || []).findIndex(step =>
      step.sections.some(section => Object.keys(validateSection(section, dadosRestaurados)).length > 0)
    );
    if (etapaInvalida !== -1 && etapaInvalida + 1 < etapa) {
      etapa = etapaInvalida + 1;
    }

    setFormData(prev => ({ ...prev, ...rascunho.formData }));
    setSolicitacaoId(rascunho.solicitacaoId || null);
    setNumeroPedido(rascunho.numeroPedido || null);
    setRequerimentoId(rascunho.requerimentoId || null);
    setCurrentStep(etapa);
  }, [totalSteps, hasDocuments, formData, config?.steps]);

  return {
    currentStep,
    formData,
    errors,
    touched,
    documentos,
    loading,
    error,
    protocoloData,
    solicitacaoId,
    numeroPedido,
    requerimentoId,
    hasDocuments,
    isDocumentStep,
    isPagamentoStep,
    isSuccessStep,
    paymentAmount,
    dynamicPaymentConfig,
    setFormData,
    setDocumentos,
    setLoading,
    setError,
    setProtocoloData,
    setSolicitacaoId,
    setNumeroPedido,
    setRequerimentoId,
    setErrors,
    setTouched,
    handleChange,
    handleBlur,
    nextStep,
    prevStep,
    resetForm,
    restaurarRascunho
  };
};